import { useState, type FormEvent } from "react";
import { copy } from "../copy";
import { adminApi, type AdminMarket, type Managed, type MarketSettings } from "./api";
import { backingCopy } from "./backingCopy";
import { useLandingFeed } from "../feed";
import { Card, hintClass, labelClass } from "./ContestForm";
import { Chip, ConfirmDialog, inputClass, primaryButton, tdClass, thClass } from "./ui";
import { producersKeep, sameSplit, tierCopy, TIER_LABEL, workedExample, type TierSplit } from "../tiers";

// The market half of the Edit contest page: open a market, tune it, add a
// house bonus, and back a producer with house money. Inputs are typed in
// percent and dollars and sent as basis points and whole cents; every
// figure shown is the server's display string.

type Settings = { fee: string; producerCut: string; backing: string; minStake: string; cap: string; seed: string };
const DEFAULT_SETTINGS: Settings = { fee: "15", producerCut: "5", backing: "5", minStake: "1", cap: "1000", seed: "0" };

const DECIMAL = /^\d+(\.\d{1,2})?$/;
// hundredths turns "15" or "2.50" into 1500 or 250: percent to basis points,
// dollars to cents. Anything else is null.
export function hundredths(v: string): number | null {
  const t = v.trim();
  return DECIMAL.test(t) ? Math.round(Number(t) * 100) : null;
}
const fromHundredths = (n: number) => String(n / 100);

// A new market's fields start on its contest tier's preset, so opening is
// still one press. Without a preset today's defaults stay.
function presetSettings(preset: TierSplit | null): Settings {
  if (!preset) return DEFAULT_SETTINGS;
  return {
    ...DEFAULT_SETTINGS,
    fee: fromHundredths(preset.rake_bps),
    producerCut: fromHundredths(preset.producer_bps),
    backing: fromHundredths(preset.backing_bps),
    seed: "0",
  };
}

function settingsOf(m: AdminMarket): Settings {
  return {
    fee: fromHundredths(m.rake_bps),
    producerCut: fromHundredths(m.producer_bps),
    backing: fromHundredths(m.backing_bps),
    minStake: fromHundredths(m.min_stake.cents),
    cap: fromHundredths(m.per_user_cap.cents),
    seed: fromHundredths(m.seed.cents),
  };
}

function toBody(s: Settings): MarketSettings | null {
  const values = [s.fee, s.producerCut, s.backing, s.minStake, s.cap, s.seed].map(hundredths);
  if (values.some((v) => v === null)) return null;
  const [rake_bps, producer_bps, backing_bps, min_stake_cents, per_user_cap_cents, seed_cents] = values as number[];
  return { rake_bps, producer_bps, backing_bps, min_stake_cents, per_user_cap_cents, seed_cents };
}

const newKey = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `house-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function MarketPanel({
  managed,
  onChange,
  onNotice,
}: {
  managed: Managed;
  onChange: (m: Managed) => void;
  onNotice: (n: { kind: "error" | "ok"; text: string }) => void;
}) {
  const t = copy.admin.market;
  const { contest, market } = managed;
  // The fee rates come from the server, so the panel never holds a copy of
  // a number Stripe or PayPal can change.
  const feed = useLandingFeed();
  const [settings, setSettings] = useState<Settings>(market ? settingsOf(market) : presetSettings(contest.tier_preset));
  const [busy, setBusy] = useState<"settings" | "back" | null>(null);
  const [producer, setProducer] = useState("");
  const [amount, setAmount] = useState("");
  const [backKey, setBackKey] = useState(newKey);
  const [confirm, setConfirm] = useState<{ user: string; amount: string; cents: number } | null>(null);

  const open = market?.status === "open";
  const takeLocked = !!market && market.stakes > 0;
  const setField = (k: keyof Settings) => (v: string) => setSettings((s) => ({ ...s, [k]: v }));

  // Home Turf has no house money at all: the platform puts nothing in, so
  // the bonus and the backing form are not offered. The server refuses them
  // too (STORY-188).
  const tier = market ? market.tier : contest.tier;
  const houseMoney = tier !== "home_turf";
  // The split as typed, when all three parse. It drives the Custom chip and
  // the worked example.
  const typedSplit = ((): TierSplit | null => {
    const values = [settings.fee, settings.producerCut, settings.backing].map(hundredths);
    if (values.some((v) => v === null)) return null;
    const [rake_bps, producer_bps, backing_bps] = values as number[];
    return { rake_bps, producer_bps, backing_bps };
  })();
  // What the platform is left with per $1 after the worst-case rails cost.
  const feeBps = hundredths(settings.fee);
  const netBps = feed?.fees && feeBps !== null ? feeBps - feed.fees.worst_case_bps : null;
  const netLine =
    netBps === null
      ? null
      : (netBps < 0 ? t.netLoses : t.netKeeps)((Math.round(Math.abs(netBps) / 10) / 10).toFixed(1));

  const isCustom = takeLocked
    ? !!market?.is_custom
    : !!contest.tier_preset && !!typedSplit && !sameSplit(typedSplit, contest.tier_preset);

  const saveSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const body = toBody(houseMoney ? settings : { ...settings, seed: "0" });
    if (!body) {
      onNotice({ kind: "error", text: t.badNumber });
      return;
    }
    setBusy("settings");
    const res = market ? await adminApi.tuneMarket(contest.id, body) : await adminApi.openMarket(contest.id, body);
    setBusy(null);
    if (!res.ok) {
      onNotice({ kind: "error", text: res.error });
      return;
    }
    onChange(res.data);
    if (res.data.market) setSettings(settingsOf(res.data.market));
    onNotice({ kind: "ok", text: market ? t.saved : t.opened });
  };

  const askBack = (e: FormEvent) => {
    e.preventDefault();
    const cents = hundredths(amount);
    const user = contest.entrants.find((p) => p.user_id === producer);
    if (!user || cents === null || cents <= 0) {
      onNotice({ kind: "error", text: t.badNumber });
      return;
    }
    setConfirm({ user: user.username || user.user_id, amount: `$${amount.trim()}`, cents });
  };

  const back = async (cents: number, user: string) => {
    setBusy("back");
    const res = await adminApi.backProducer(contest.id, producer, cents, backKey);
    setBusy(null);
    if (!res.ok) {
      onNotice({ kind: "error", text: res.error });
      return;
    }
    setBackKey(newKey());
    setAmount("");
    onChange(res.data);
    onNotice({ kind: "ok", text: t.backed(user) });
  };

  const percentField = (id: string, label: string, hint: string, key: keyof Settings, disabled: boolean) => (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        inputMode="decimal"
        disabled={disabled}
        value={settings[key]}
        onChange={(e) => setField(key)(e.target.value)}
        className={`${inputClass} bf-tabular disabled:cursor-not-allowed disabled:opacity-60`}
      />
      {hint && <p className={hintClass}>{hint}</p>}
    </div>
  );

  return (
    <Card title={t.heading} icon="coins">
      <div className="flex flex-wrap items-center gap-2">
        {market && <Chip tone={open ? "money" : market.status === "voided" ? "alert" : "gold"}>{t.states[market.status]}</Chip>}
        <Chip tone="dim">{TIER_LABEL[tier]}</Chip>
        {isCustom && (
          <span data-testid="tier-custom">
            <Chip tone="gold">{tierCopy.custom}</Chip>
          </span>
        )}
      </div>

      {!market && <p className="text-[13px] leading-relaxed text-chrome-dim">{t.none}</p>}

      {market && (
        <div className="grid grid-cols-2 gap-3">
          {(
            [
              ["pool", market.pool.display, "text-gold"],
              ...(houseMoney
                ? ([
                    ["bonus", market.seed.display, "text-money"],
                    ["house", market.house.display, "text-chrome"],
                  ] as const)
                : []),
              ["picks", String(market.stakes), "text-chrome"],
            ] as const
          ).map(([key, value, tone]) => (
            <div key={key} className="min-w-0 rounded-lg border border-ground-edge bg-ground p-3">
              <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{t.tiles[key]}</p>
              <p data-testid={`market-${key}`} className={`bf-tabular mt-1.5 truncate font-headline text-xl leading-none ${tone}`}>
                {value}
              </p>
            </div>
          ))}
        </div>
      )}

      {market?.status === "voided" && (
        <p className="rounded-lg border border-ground-edge bg-ground px-4 py-3 text-[13px] text-chrome">{t.voided(market.void_reason)}</p>
      )}

      {(!market || open) && (
        <form onSubmit={(e) => void saveSettings(e)} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              {percentField("market-fee", t.fee, t.feeHint, "fee", takeLocked)}
              {netLine && (
                <p data-testid="market-net" className="mt-1.5 text-[12px] text-chrome-dim">
                  {netLine}
                </p>
              )}
            </div>
            {percentField("market-producer", t.producerCut, t.producerCutHint, "producerCut", takeLocked)}
            {percentField("market-backing", backingCopy.field, backingCopy.hint, "backing", takeLocked)}
            {percentField("market-min", t.minStake, "", "minStake", false)}
            {percentField("market-cap", t.cap, "", "cap", false)}
          </div>
          {takeLocked && <p className="text-[12px] text-gold">{backingCopy.takeLocked}</p>}
          {typedSplit && (
            <div data-testid="tier-preview" className="space-y-1 rounded-lg border border-ground-edge bg-ground p-3 text-[12px] text-chrome-dim">
              {(() => {
                const e = workedExample(typedSplit);
                return (
                  <>
                    <p className="font-bold text-money">{producersKeep(typedSplit)}</p>
                    <p>{tierCopy.previewPot}</p>
                    <p>{tierCopy.previewBacking(e)}</p>
                    <p>{tierCopy.previewWinner(e)}</p>
                    <p>{tierCopy.previewFans(e)}</p>
                    <p>{tierCopy.previewPlatform(e)}</p>
                  </>
                );
              })()}
            </div>
          )}
          {houseMoney && percentField("market-seed", t.seed, t.seedHint, "seed", false)}
          <button type="submit" disabled={busy !== null} className={`${primaryButton} w-full`}>
            {busy === "settings" ? (market ? t.saving : t.opening) : market ? t.save : t.open}
          </button>
        </form>
      )}

      {market && (
        <div className="overflow-x-auto">
          {market.outcomes.length === 0 ? (
            <p className="text-[13px] text-chrome-dim">{t.noOutcomes}</p>
          ) : (
            <table className="w-full min-w-[420px]">
              <thead>
                <tr className="border-b border-ground-edge">
                  <th className={thClass}>{t.columns.producer}</th>
                  <th className={`${thClass} text-right`}>{t.columns.staked}</th>
                  <th className={`${thClass} text-right`}>{t.columns.house}</th>
                  <th className={`${thClass} text-right`}>{t.columns.backers}</th>
                  <th className={`${thClass} text-right`}>{t.columns.odds}</th>
                </tr>
              </thead>
              <tbody>
                {market.outcomes.map((o) => (
                  <tr key={o.producer_id} data-testid="market-outcome" className="border-b border-ground-edge/60 last:border-0">
                    <td className={tdClass}>{o.username || o.producer_id}</td>
                    <td className={`${tdClass} bf-tabular text-right`}>{o.staked.display}</td>
                    <td className={`${tdClass} bf-tabular text-right text-chrome-dim`}>{o.house.display}</td>
                    <td className={`${tdClass} bf-tabular text-right`}>{o.backers}</td>
                    <td className={`${tdClass} bf-tabular text-right font-bold text-gold`}>{o.odds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {open && houseMoney && (
        <form onSubmit={askBack} className="space-y-4 rounded-xl border border-ground-edge bg-ground p-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-chrome">{t.back}</p>
            <p className={hintClass}>{t.backHint}</p>
          </div>
          {contest.entrants.length === 0 ? (
            <p className="text-[13px] text-chrome-dim">{t.noEntrants}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="back-producer" className={labelClass}>
                    {t.producer}
                  </label>
                  <select
                    id="back-producer"
                    value={producer}
                    onChange={(e) => setProducer(e.target.value)}
                    className={`${inputClass} cursor-pointer`}
                  >
                    <option value="">{t.choose}</option>
                    {contest.entrants.map((p) => (
                      <option key={p.user_id} value={p.user_id}>
                        {p.username || p.user_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="back-amount" className={labelClass}>
                    {t.amount}
                  </label>
                  <input
                    id="back-amount"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className={`${inputClass} bf-tabular`}
                  />
                </div>
              </div>
              <button type="submit" disabled={busy !== null || !producer || !amount} className={`${primaryButton} w-full`}>
                {busy === "back" ? t.backing : t.backButton}
              </button>
            </>
          )}
        </form>
      )}

      {confirm && (
        <ConfirmDialog
          message={t.confirmBack(confirm.user, confirm.amount)}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const c = confirm;
            setConfirm(null);
            void back(c.cents, c.user);
          }}
        />
      )}
    </Card>
  );
}
